import React, { forwardRef, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Slide from '@mui/material/Slide';
import { useGetTicketByIdQuery } from 'src/app/services/ticketsApi';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faX } from '@fortawesome/free-solid-svg-icons';
import { Avatar } from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import clsx from 'clsx';
import { getAbsoluteFileUrl } from 'src/app/helpers/utils';
import { TASK_MEMBER_ROLE } from '../../../projects/vars';
import StaffAvatarGroup from 'app/shared-components/StaffAvatarGroup';
import _ from 'lodash';
import { useThemeMediaQuery } from '@fuse/hooks';
import { styled } from '@mui/material/styles';
import './TaskCards.css';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: '800px',
    height: '87vh',
    backgroundColor: localStorage.getItem('isDarkMode') === 'true' ? '#16141a' : '#fff',
    '@media (max-width: 600px)': {
      maxWidth: '100%',
      height: 'auto',
      padding: '20px',
    },
  },
}));

const DetailedTaskCard = ({
  open,
  taskId,
  onClose,
  title,
  isEditButtonVisible,
  onEditButtonClick,
  disableTransition = false,
}) => {
  const { t } = useTranslation('translations');
  const { data: task = null, isError } = useGetTicketByIdQuery({ id: taskId }, { skip: !taskId });
  const members = task?.members || [];
  const assigned = members.filter((member) => member.role === TASK_MEMBER_ROLE.EXECUTOR);
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('sm'));
  const getStatusText = (status) => {
    const statusMapping = {
      0: 'New',
      1: 'InWork',
      2: 'Closed',
    };
    return statusMapping[status] || 'Unknown Status';
  };
  const getPriorityText = (priority) => {
    const priorityMapping = {
      0: 'Low',
      1: 'Usual',
      2: 'High',
      3: 'Urgent',
    };
    return priorityMapping[priority] || 'Unknown priority';
  };
  const handleClose = (_event, reason) => {
    if (reason === 'escapeKeyDown') {
      onClose();
    } else if (isMobile) {
      onClose();
    }
  };
  return (
    <StyledDialog
      open={open}
      {...(disableTransition ? {} : { TransitionComponent: Transition })}
      aria-describedby="alert-dialog-slide-description"
      onClose={handleClose}
    >
      <div className="flex flex-col h-full gap-10 p-0 text-black sm:flex-row dark:text-white ">
        {isError ? (
          <div className="flex items-center justify-center w-full h-192 sm:h-full text-16 sm:text-24 md:text-28">
            <p>{t('TaskNotFoundMightBeChanged')}</p>
          </div>
        ) : (
          <div className="justify-center w-full flex-column max-w-580">
            <div className="detailedcard__navbar">
              <h2
                className="mt-32 detailedcard__navbar-title sm:mt-0"
                style={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                {task?.projectName} {'>'} {`${t(title)}`}
              </h2>
              <div className="detailedcard__navbar-avatarGroup">
                <div className="detailedcard__navbar-executors">{t('Executors')}: </div>
                <StaffAvatarGroup addButton members={assigned} title={t('Executors')} />
              </div>
            </div>
            <div className="detailedcard__cards">
              {task?.phaseTitle !== null && (
                <div className="detailedcard__cards-phaseContent">
                  <p className="detailedcard__cards-desc">{t(`${task?.phaseTitle}`)}</p>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('Phase')}
                  </span>
                </div>
              )}
              <div className="flex flex-col gap-20 mt-20 sm:flex-row">
                <div className="detailedcard__cards-time">
                  <p className="detailedcard__cards-desc">
                    {moment(task?.startDate).format('DD.MM.YYYY HH:mm')}
                  </p>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('StartDate')}
                  </span>
                </div>
                <div className="detailedcard__cards-time">
                  <p className="detailedcard__cards-desc">
                    {task?.endDate ? moment(task?.endDate).format('DD.MM.YYYY HH:mm') : ''}
                  </p>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('EndDate')}
                  </span>
                </div>
              </div>
              <div className="detailedcard__cards-title">
                <p className="detailedcard__cards-desc">{task?.title}</p>
                <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                  {t('Title')}
                </span>
              </div>
              {task?.description && (
                <div className="detailedcard__cards-overview">
                  <div className="detailedcard__cards-overview_overflow">
                    <p className="detailedcard__cards-desc">{task?.description}</p>
                  </div>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('Description')}
                  </span>
                </div>
              )}
              <div className="detailedcard__cards-package">
                <div className="detailedcard__cards-package_content">
                  <p>{t(`${getPriorityText(task?.taskPriority)}`)}</p>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('Priority')}
                  </span>
                </div>
                <div className="detailedcard__cards-package_content">
                  <p>{t(`${getStatusText(task?.status)}`)}</p>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('Status')}
                  </span>
                </div>
                <div className="detailedcard__cards-package_content">
                  <p>{task?.progress}</p>
                  <span className="absolute -top-12 left-10  px-2 text-[12px] bg-white dark:bg-[#16141a]">
                    {t('Completed')} %
                  </span>
                </div>
              </div>
              {task?.comment?.length > 0 ? (
                <div>
                  <div className="flex gap-10 mt-20">
                    <FuseSvgIcon size={20}>react-outline:chat-alt</FuseSvgIcon>
                    <h2 className="text-[16px]">{t('Comments')}</h2>
                  </div>
                  {task.comment.map((item, index) => {
                    const commentedUser = item;

                    return (
                      <div className="detailedcard__comments" key={index}>
                        <Avatar
                          src={
                            commentedUser?.photo ? getAbsoluteFileUrl(commentedUser?.photo) : null
                          }
                          sx={{ maxWidth: '30px', width: '100%', height: '30px' }}
                        />
                        <div className="detailedcard__comments-item">
                          <p className="detailedcard__comments-fio">{commentedUser?.name}</p>
                          <p className="detailedcard__comments-desc">{item.comment}</p>
                          <p className="detailedcard__comments-date">
                            {moment(item.createdDate).format('DD.MM.YYYY HH:mm')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                ''
              )}
            </div>
          </div>
        )}

        <div className={clsx('detailedcard__buttons-container', 'bg-white dark:bg-dark')}>
          <div className={clsx('detailedcard__buttons-content', 'bg-grey-100 dark:bg-darker')}>
            <button
              onClick={onClose}
              className="detailedcard__buttons-btn hover:bg-grey-200 dark:hover:bg-[#16151C] text-16"
            >
              <FontAwesomeIcon icon={faX} />
            </button>
            {isEditButtonVisible && (
              <button
                onClick={onEditButtonClick}
                className="detailedcard__buttons-btn hover:bg-grey-200 dark:hover:bg-[#16151C] text-16"
              >
                <FontAwesomeIcon icon={faPenToSquare} />
              </button>
            )}
          </div>
        </div>
      </div>
    </StyledDialog>
  );
};

export default DetailedTaskCard;
